/* eslint-disable no-console */
// Port of dataTableSparkline.column.factory
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import _ from 'lodash';
import { useTheme } from '@material-ui/core/styles';

import { GetTooltip } from '../../charts/tooltip/GetTooltip';
import Tooltip from '../../tooltip/Tooltip';
import { connectIsolatedPointsNearest } from '../../charts/performance-chart/funcs/SeriesTransform';
import useSingletonValue from '../../../utilities/UseSingletonValue';

const markerDefaults = {
    /**
     * The opacity of the circle indicators
     */
    opacity: 1,

    /**
     * The radius of the circle indicators
     */
    radius: 2.5,

    /**
     * @cfg {Number} strokeWidth
     *
     * The stroke of the circle indicators
     */
    strokeWidth: 1,

    /**
     * The opacity of the vertical marker line
     */
    lineOpacity: 1,

    /**
     * The color of the vertical marker line
     */
    lineStroke: '#555555',

    /**
     * The radius of the marker halo
     */
    haloRadius: 10,

    /**
     * The opacity of the halo. Set to 0 to disable the halo.
     */
    haloOpacity: 0.3,

    /**
     * The stroke width of the vertical marker line
     */
    lineStrokeWidth: 0.7,
};

function prepareChart({ containerEl, chartDef, data, margin, onMouseOver }) {
    const series = buildSeries(chartDef, data);
    const seriesList = series;
    const granularity = getGranularity(series);
    const domainX = getXExtent(series);
    const domainY = getYExtent(chartDef, series);

    const svg = d3.select(containerEl).append('svg');

    const marker = _.cloneDeep(markerDefaults);
    const chart = svg.append('g');
    chart.attr('transform', `translate(0, ${margin})`);

    const markerGroup = svg.append('g');
    markerGroup.attr('transform', `translate(0, ${margin})`);

    const xAxis = chart.append('line').attr('class', 'x-axis'); // css classes from d3
    const markerLine = markerGroup.append('line').attr('class', 'marker-line');
    const mouseRect = svg.append('rect');

    function reflow({ width, height }) {
        const totalHeight = height + margin * 2;

        svg.attr('width', width);
        svg.attr('height', totalHeight);

        const x = d3.scaleLinear().range([0, width]).domain(domainX);
        const y = d3.scaleLinear().range([height, 0]).domain(domainY);

        // point value accessors
        const vx = (d) => x(d.x);
        const vy0 = (d) => y(d.y0);
        const vy1 = (d) => y(d.y1);
        const vDefined = (d) => d && _.isFinite(d.y0) && _.isFinite(d.y1);

        // create the path generators
        const curveFactory = d3.curveMonotoneX;
        const overlayGenerator = d3.line().x(vx).y(vy1).defined(vDefined).curve(curveFactory);
        const areaGenerator = d3.area().x(vx).y0(vy0).y1(vy1).defined(vDefined).curve(curveFactory);

        xAxis
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', height)
            .attr('y2', height)
            .style('stroke', '#CCCCCC')
            .style('shape-rendering', 'crispEdges')
            .style('stroke-width', 1);

        // this is the filled in portion of the chart
        const areaPaths = chart.selectAll('path.area').data(seriesList);
        areaPaths
            .enter()
            .append('path')
            .attr('class', 'area')
            .merge(areaPaths)
            .attr('d', (d) => areaGenerator(d.data))
            .style('fill', (d) => d.color)
            .style('fill-opacity', (d) => d.fillOpacity);

        // this is the overlay line above the area portion
        const overlayLines = chart.selectAll('path.overlay-line').data(seriesList);
        overlayLines
            .enter()
            .append('path')
            .attr('class', 'overlay-line')
            .merge(overlayLines)
            .attr('d', (d) => overlayGenerator(d.data))
            .style('fill', 'none')
            .style('stroke-opacity', (d) => d.strokeOpacity)
            .style('stroke', (d) => d.color)
            .style('stroke-width', (d) => d.strokeWidth)
            .style('stroke-linecap', (d) => d.strokeLinecap);

        // always hide hover markers at first
        markerGroup.style('display', 'none');

        markerLine
            .attr('y1', 0)
            .attr('y2', height)
            .style('stroke', marker.lineStroke)
            .style('stroke-width', marker.lineStrokeWidth)
            .style('opacity', marker.lineOpacity);

        mouseRect
            .attr('width', width)
            .attr('height', totalHeight)
            .style('fill', 'none')
            .style('stroke', 'none')
            .style('pointer-events', 'all')
            .on('mousemove', onMouseMove)
            .on('mouseout', onMouseOut);

        function onMouseMove(event) {
            const [mouseX] = d3.pointer(event);
            const invertedX = x.invert(mouseX);
            const pointKey = findNearsetTooltipPointKey(seriesList, invertedX);
            const timeslice = getTimeslice(seriesList, pointKey, granularity);
            const markerLineX = pointKey || invertedX;
            const minY = _.minBy(timeslice.items, (t) => t.y);
            const maxY = _.maxBy(timeslice.items, (t) => t.y);
            const minMarkerLineY = y(minY ? minY.y : 0);
            const maxMarkerLineY = y(maxY ? maxY.y : 0);

            markerLine.attr('x1', x(markerLineX)).attr('x2', x(markerLineX));
            markerLine.attr('y1', maxMarkerLineY - marker.haloRadius);
            markerLine.attr('y2', minMarkerLineY + marker.haloRadius);
            markerGroup.style('display', null);
            markPoints(timeslice.items.filter((point) => _.isNumber(point.value)));
            onMouseOver(timeslice);
        }

        // places markers over an array of points
        function markPoints(points) {
            const outerSel = markerGroup.selectAll('circle.marker-circle-outer').data(points);
            const innerSel = markerGroup.selectAll('circle.marker-circle-inner').data(points);

            outerSel.exit().remove();
            innerSel.exit().remove();

            // first let's handle the outer circle markers (halos)
            outerSel
                .enter()
                .append('circle')
                .attr('class', 'circle-marker marker-circle-outer')
                .merge(outerSel)
                .attr('r', marker.haloRadius)
                .attr('fill', (d) => d.color)
                .attr('stroke', 'none')
                .attr('opacity', marker.haloOpacity);

            // now handle the inner circle markers
            innerSel
                .enter()
                .append('circle')
                .attr('class', 'circle-marker marker-circle-inner')
                .merge(innerSel)
                .attr('r', marker.radius)
                .attr('fill', (d) => d.color)
                .attr('stroke', 'white')
                .attr('stroke-width', marker.strokeWidth);

            markerGroup.selectAll('circle.circle-marker').attr('transform', (d) => `translate(${x(d.x)}, ${y(d.y1)})`);
        }

        function onMouseOut() {
            markerGroup.style('display', 'none');
        }
    }

    return reflow;
}

export default function SparklineColumn({ height, width, margin, chartDef: initialChartDef, data }) {
    const [timeslice, setTimeslice] = useState();
    const reflowCallback = useRef();
    const theme = useTheme();

    // Note, you cannot change the chartDef on the fly. Once it's set, that's it.
    const chartDef = useSingletonValue(() => initialChartDef);

    const onMouseOver = useCallback((tslice) => setTimeslice({ ...tslice, chartDef }), [chartDef]);

    useLayoutEffect(() => {
        if (!reflowCallback.current) return;
        reflowCallback.current({ width: width - theme.spacing(2), height });
    }, [width, height, theme]);

    const setChartContainer = useSingletonValue(() => (containerEl) => {
        if (!containerEl) return;

        reflowCallback.current = prepareChart({
            containerEl,
            chartDef,
            data,
            margin,
            height,
            onMouseOver,
        });
    });

    return (
        <Tooltip offset={50} title={<GetTooltip chartDef={chartDef} timeslice={timeslice} />} placement="left">
            <div ref={setChartContainer} />
        </Tooltip>
    );
}

export function sparklinkColumnRenderer(rowData, column) {
    const { chartDef, data } = column.mapper(rowData, column);

    return <SparklineColumn height={20} width={column.width} chartDef={chartDef} data={data} margin={10} />;
}

function isValidTooltipPoint(point) {
    return isValidPoint(point) || isValidPlotPoint(point);
}

function isValidPoint(point) {
    return point && _.isFinite(point.value);
}

/**
 * Returns whether or not a given point has a valid plotting value
 *
 * @param {Object} point
 * @return {Boolean}
 */
function isValidPlotPoint(point) {
    return point && _.isFinite(point.y || point.y1);
}

function findNearsetTooltipPointKey(chartSeries, invertedValue, keyProperty = 'x') {
    const domain = _(chartSeries)
        .map('data')
        .flatten()
        .filter(isValidTooltipPoint)
        .map(keyProperty)
        .uniq()
        .sortBy()
        .value();

    let nearestKey = domain[0];

    for (let i = 1; i < domain.length; i++) {
        const left = domain[i - 1];
        const right = domain[i];

        // if invertedValue is within the range of two X values
        if (left && right && invertedValue >= left && invertedValue <= right) {
            // pick the nearest side, left or right
            nearestKey = invertedValue - left < right - invertedValue ? left : right;
            break;
        } else if (i === domain.length - 1 && right < invertedValue) {
            // if we're at the end, pick the very last item
            nearestKey = right;
        }
    }

    return nearestKey;
}

const getSeries = (def, allData) => {
    const trend = allData?.trends?.[def.key] || {};
    const data = normalizeTrendData(trend.data).map((point) => ({
        color: def.color,
        name: def.label,
        label: def.label,
        def,
        value: point.value,
        pointData: point,
        x: point.date,
        y: point.value,
        y0: 0,
        y1: point.value,
    }));

    return {
        color: def.color,
        granularity: Number.isFinite(trend.granularity) ? trend.granularity : undefined,
        data,
        name: def.name,
        fillOpacity: 0.3,
        strokeLinecap: 'butt',
        strokeOpacity: 1,
        strokeWidth: 1,
    };
};

const getTimeslice = (series, date, granularity) => {
    const items = series
        .map((item) => item.data)
        .flat()
        .filter((item) => item.x === date);

    return {
        items,
        status: undefined,
        x: date,
        granularity,
        checkAllNull: true,
    };
};

const buildSeries = (chartDef, data) => {
    return (chartDef?.chartTrends || [])
        .map((def) => getSeries(def, data))
        .map((series) => connectIsolatedPointsNearest(series));
};

const getXExtent = (seriesList) => {
    const xes = seriesList
        .map((series) => series.data)
        .flat()
        .map((slot) => slot.x);
    const max = xes.length ? Math.max(...xes) : 0;
    const min = xes.length ? Math.min(...xes) : 0;
    return [min, max];
};

const getYExtent = (chartDef, seriesList) => {
    // The Y extent is 0 to either the largest 'max' value set in the chart trend configs or else
    // the largest y value in the data (if max is not set).
    const yes = seriesList
        .map((series) => series.data)
        .flat()
        .map((slot) => slot.y)
        .filter(Number.isFinite);
    const chartTrendMax = _.maxBy(chartDef.chartTrends, (t) => t.max);
    const defMax = chartTrendMax ? chartTrendMax.max || 0 : 0;
    const max = yes.length ? Math.max(defMax, ...yes) : defMax;
    return [0, max];
};

const getGranularity = (series) => {
    const grans = series.map((item) => item.granularity).filter(Number.isFinite);
    return Math.max(60_000, ...grans);
};

function normalizeTrendData(data) {
    if (!Array.isArray(data)) return [];
    return data.filter((point) => point && Number.isFinite(point.date));
}
